import type { TFunction } from "i18next";

export interface FriendlyReasoningSection {
  title: string;
  content: string;
}

interface ReasoningEntry {
  key: string;
  value: string;
}

interface ReasoningDisplay {
  reasoning?: string;
  reasoningSections?: FriendlyReasoningSection[];
}

const normalizeReasoning = (text: string): string => {
  if (!text) return "";
  return text.replace(/\\n/g, "\n").replace(/\r\n/g, "\n").trim();
};

const looksLikeStructuredReasoning = (text: string): boolean =>
  text.trim().startsWith("{") ||
  /["']?(thinking|thiking|query|queries|to_request|continue_retrieve|need_retrieval)["']?\s*:/i.test(
    text,
  );

const findClosingQuote = (text: string, start: number, quote: string): number => {
  let escaped = false;

  for (let i = start; i < text.length; i += 1) {
    const char = text[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (char === "\\") {
      escaped = true;
      continue;
    }

    if (char !== quote) continue;

    const rest = text.slice(i + 1).trimStart();
    if (!rest || rest.startsWith(",") || rest.startsWith("}") || rest.startsWith("]")) {
      return i;
    }
  }

  return -1;
};

const decodeReasoningValue = (value: string): string =>
  value
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\")
    .trim();

const parseReasoningValue = (text: string, start: number): { value: string; nextIndex: number } => {
  let cursor = start;
  while (cursor < text.length && /\s/.test(text[cursor])) cursor += 1;

  const quote = text[cursor];
  if (quote === '"' || quote === "'") {
    const valueStart = cursor + 1;
    const closing = findClosingQuote(text, valueStart, quote);
    const rawValue = closing >= 0 ? text.slice(valueStart, closing) : text.slice(valueStart);
    return {
      value: decodeReasoningValue(rawValue),
      nextIndex: closing >= 0 ? closing + 1 : text.length,
    };
  }

  let end = cursor;
  while (end < text.length && ![",", "}", "]", "\n"].includes(text[end])) end += 1;

  return {
    value: decodeReasoningValue(text.slice(cursor, end)),
    nextIndex: end,
  };
};

const extractReasoningEntries = (text: string): ReasoningEntry[] => {
  const entries: ReasoningEntry[] = [];
  const keyRegex = /["']?([A-Za-z][A-Za-z0-9_-]*)["']?\s*:/g;

  let match: RegExpExecArray | null;
  while ((match = keyRegex.exec(text)) && entries.length < 8) {
    const key = match[1];
    const parsed = parseReasoningValue(text, match.index + match[0].length);

    if (key && parsed.value) {
      entries.push({ key, value: parsed.value });
    }

    keyRegex.lastIndex = Math.max(parsed.nextIndex, match.index + match[0].length + 1);
  }

  return entries;
};

const normalizeReasoningKey = (key: string): string =>
  key
    .trim()
    .replace(/[-\s]+/g, "_")
    .toLowerCase();

const prettifyReasoningKey = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getReasoningSectionTitle = (key: string, t: TFunction): string => {
  const normalized = normalizeReasoningKey(key);

  if (normalized === "thinking" || normalized === "thiking") {
    return t("opsmate.progress.reasoning.section.thinking");
  }

  if (normalized === "query" || normalized === "queries") {
    return t("opsmate.progress.reasoning.section.query");
  }

  if (normalized === "to_request") {
    return t("opsmate.progress.reasoning.section.toRequest");
  }

  if (normalized === "continue_retrieve" || normalized === "need_retrieval") {
    return t("opsmate.progress.reasoning.section.retrievalDecision");
  }

  if (normalized === "rationale" || normalized === "reason") {
    return t("opsmate.progress.reasoning.section.rationale");
  }

  if (normalized === "answer") {
    return t("opsmate.progress.reasoning.section.answer");
  }

  return t("opsmate.progress.reasoning.section.default", {
    label: prettifyReasoningKey(key),
  });
};

const formatBooleanLikeValue = (value: string, t: TFunction): string => {
  const normalized = value.trim().toLowerCase();

  if (["yes", "true", "y"].includes(normalized)) {
    return t("opsmate.progress.boolean.yes");
  }

  if (["no", "false", "n"].includes(normalized)) {
    return t("opsmate.progress.boolean.no");
  }

  return value;
};

export const formatReasoningForDisplay = (text: string, t: TFunction): ReasoningDisplay => {
  const normalized = normalizeReasoning(text);
  if (!normalized) return {};

  const entries = extractReasoningEntries(normalized);

  if (entries.length > 0) {
    return {
      reasoningSections: entries.map((entry) => ({
        title: getReasoningSectionTitle(entry.key, t),
        content: formatBooleanLikeValue(entry.value, t),
      })),
    };
  }

  if (looksLikeStructuredReasoning(normalized)) return {};

  return { reasoning: normalized };
};
